import React from "react";
import { ExternalLink } from "lucide-react";
import Section from "@/theme/Section";
import { useContent } from "@/lib/content";

const ACCENTS = ["#c8ff2a", "#ff2a8f", "#2ad6ff", "#ffce3a"];

/**
 * Reusable section card grid used by /work for both client work and games.
 * If `section` prop given, fetch that section from context. Otherwise pass items.
 */
const GameGrid = ({
  section,
  eyebrow,
  eyebrowColor,
  title,
  subtitle,
  emptyText = "Coming soon",
  testid,
}) => {
  const { sections } = useContent();
  const s = sections?.[section] || {};
  if (!s.enabled) return null;
  const items = (s.items || []).filter((i) => i.enabled);
  return (
    <Section id={section} testid={testid} eyebrow={eyebrow} eyebrowColor={eyebrowColor} title={title} subtitle={subtitle}>
      {items.length === 0 ? (
        <div
          className="text-center text-white/60 py-12 border border-dashed rounded-2xl"
          style={{ borderColor: "rgba(255,255,255,0.15)" }}
        >
          {emptyText}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((g, i) => {
            const accent = ACCENTS[i % ACCENTS.length];
            const playLabel = section === "assets" ? "Asset Store" : "Google Play";
            const links = [
              g.playStoreLink && { label: playLabel, url: g.playStoreLink },
              g.appStoreLink && { label: "App Store", url: g.appStoreLink },
              g.steamLink && { label: "Steam", url: g.steamLink },
            ].filter(Boolean);
            const primaryLink = links[0]?.url || "#";

            return (
              <div
                key={g.id}
                data-testid={`game-card-${section}-${i}`}
                className="group overflow-hidden transition-transform hover:-translate-y-1"
                style={{
                  background: "rgba(13,13,13,0.7)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "24px",
                  backdropFilter: "blur(14px)",
                }}
              >
                <a
                  href={primaryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className={`relative overflow-hidden ${section === "assets" ? "aspect-[3/2]" : "aspect-square"}`}>
                    {g.icon ? (
                      <img
                        src={g.icon}
                        alt={g.title}
                        className="w-full h-full object-cover object-left-top transition-transform duration-700 group-hover:scale-105"
                        style={(g.iconPaddingLeft || g.iconPaddingTop) ? { objectPosition: `${g.iconPaddingLeft || 0}px ${g.iconPaddingTop || 0}px` } : undefined}
                      />
                    ) : (
                      <div
                        className="w-full h-full"
                        style={{
                          background: `linear-gradient(135deg, ${accent}, #060606)`,
                        }}
                      />
                    )}
                    <div
                      className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center"
                      style={{
                        background: "rgba(6,6,6,0.7)",
                        border: `1px solid ${accent}`,
                        borderRadius: "999px",
                      }}
                    >
                      <ExternalLink size={14} style={{ color: accent }} />
                    </div>
                  </div>
                </a>
                <div className="p-5">
                  <h3
                    className="font-black uppercase text-lg"
                    style={{
                      fontFamily: "'Azeret Mono', monospace",
                      letterSpacing: "-0.03em",
                      color: "#fff",
                    }}
                  >
                    {g.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {links.map((l) => (
                      <a
                        key={l.label}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold transition-colors hover:opacity-80"
                        style={{
                          background: "rgba(255,255,255,0.08)",
                          border: `1px solid ${accent}`,
                          color: accent,
                        }}
                      >
                        {l.label} <ExternalLink size={10} />
                      </a>
                    ))}
                    {links.length === 0 && (
                      <span
                        className="text-[10px] tracking-[0.28em] uppercase font-bold"
                        style={{ color: "rgba(255,255,255,0.4)" }}
                      >
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

export default GameGrid;
